export type CollaboratorRole = "read" | "write";

export type TripRole = "owner" | CollaboratorRole;

export type Travelers = {
  adults: number;
  children: number[];
};

export type MustVisitEntry = {
  id: string;
  name: string;
};

export type TripCoverPhoto = {
  destinationId: string;
  photoUrl: string;
  lat: number;
  lng: number;
};

export type Trip = {
  id: string;
  ownerId: string;
  title: string;
  description: string | null;
  startDate: string | null;
  endDate: string | null;
  startLocation: string | null;
  endLocation: string | null;
  travelers: Travelers | null;
  travelersNotes: string | null;
  interests: string | null;
  mustVisits: MustVisitEntry[] | null;
  maxDrivingHoursPerDay: number;
  headerNotes?: string | null;
  summaryNotes?: string | null;
  archived: boolean;
  /**
   * Only present on the GET /api/trips list response (powers the home page's
   * Delete-vs-Archive button switch). Undefined on POST/PATCH responses.
   */
  hasBookings?: boolean;
  /** Count of destination rows for this trip (list response only). */
  stops?: number;
  /** Sum of route_cache.distance_m across segments, null when no routes resolved (list response only). */
  distanceMeters?: number | null;
  createdAt: string;
  updatedAt: string;
  role: TripRole;
  collaboratorCount: number;
  photoUrl?: string | null;
  coverPhotos: TripCoverPhoto[];
  ownerName?: string;
  ownerImage?: string | null;
  editors?: Array<{ name: string; image: string | null }>;
};

/**
 * Canonical display string for a Travelers value, e.g.
 * "2 adults, 3 children (3, 4, 8)". Returns null when the structure has
 * zero head count so callers can render an empty-state placeholder.
 */
export function formatTravelers(t: Travelers | null | undefined): string | null {
  if (!t) return null;
  const total = t.adults + t.children.length;
  if (total === 0) return null;
  const parts: string[] = [];
  if (t.adults > 0) {
    parts.push(`${t.adults} ${t.adults === 1 ? "adult" : "adults"}`);
  }
  if (t.children.length > 0) {
    parts.push(
      `${t.children.length} ${t.children.length === 1 ? "child" : "children"} (${t.children.join(", ")})`
    );
  }
  return parts.join(", ");
}

export type Collaborator = {
  id: string;
  tripId: string;
  userId: string;
  role: CollaboratorRole;
  invitedBy: string;
  createdAt: string;
  email: string;
  name: string;
};

/**
 * Phase 9 #02 — invites carry exactly one of (email, label). The `kind`
 * discriminator lets the UI render the right header (`alice@x.com` vs
 * `Link · <label>`) without reading from null fields.
 */
export type Invite = {
  id: string;
  tripId: string;
  email: string | null;
  label: string | null;
  kind: "email" | "link";
  role: CollaboratorRole;
  invitedBy: string;
  createdAt: string;
};

export type CreateLinkInviteResponse = {
  invite: Invite;
  /** Plaintext URL — only the link-invite path returns this. */
  url: string;
};

export type InvitePending = {
  tripTitle: string;
  inviterName: string;
  role: CollaboratorRole;
};

async function request(path: string, init?: RequestInit) {
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    if (res.status === 403) {
      const body = await res.json().catch(() => null) as { error?: string } | null;
      if (body?.error === "ACCOUNT_PAUSED") {
        throw new Error("ACCOUNT_PAUSED");
      }
    }
    throw new Error(`Request failed: ${res.status}`);
  }
  return res.json();
}

export async function listTrips(
  options: { includeArchived?: boolean } = {}
): Promise<Trip[]> {
  const path = options.includeArchived ? "/trips?includeArchived=1" : "/trips";
  const body = (await request(path)) as {
    trips: Trip[];
    lastOpenedTripId?: string | null;
  };
  return body.trips;
}

export async function getTrip(id: string): Promise<Trip> {
  const body = (await request(`/trips/${id}`)) as { trip: Trip };
  return body.trip;
}

export type TripsListing = {
  trips: Trip[];
  lastOpenedTripId: string | null;
};

export async function listTripsWithMeta(
  options: { includeArchived?: boolean } = {}
): Promise<TripsListing> {
  const path = options.includeArchived ? "/trips?includeArchived=1" : "/trips";
  const body = (await request(path)) as {
    trips: Trip[];
    lastOpenedTripId: string | null;
  };
  return {
    trips: body.trips,
    lastOpenedTripId: body.lastOpenedTripId ?? null,
  };
}

export async function touchTrip(tripId: string): Promise<void> {
  await request(`/trips/${tripId}/touch`, { method: "POST" });
}

export async function createTrip(input: { title?: string; maxDrivingHoursPerDay?: number; startLocation?: string; endLocation?: string; turnstileToken?: string } = {}): Promise<Trip> {
  const res = await fetch("/api/trips", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null;
    const code = body?.error;
    const knownCodes = new Set(["ACCOUNT_PAUSED", "ANON_IP_LIMIT", "ANON_TURN_BUDGET_EXHAUSTED", "ANON_DISABLED", "TURNSTILE_REQUIRED", "TURNSTILE_FAILED"]);
    if (code && knownCodes.has(code)) throw new Error(code);
    throw new Error(`Request failed: ${res.status}`);
  }
  const body = (await res.json()) as { trip: Trip };
  return body.trip;
}

export async function claimAnonymousTrips(anonymousUserId: string): Promise<number> {
  const body = (await request("/trips/claim-anonymous", {
    method: "POST",
    body: JSON.stringify({ anonymousUserId }),
  })) as { claimed: number };
  return body.claimed;
}

export async function deleteTrip(id: string): Promise<void> {
  await request(`/trips/${id}`, { method: "DELETE" });
}

export async function downloadTripPdf(id: string): Promise<Blob> {
  const res = await fetch(`/api/trips/${id}/pdf`, {
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error((body as { error?: string }).error ?? `Request failed: ${res.status}`);
  }
  return res.blob();
}

export type CopyTripParams = {
  title?: string;
  startDate?: string;
  interests?: string;
};

export async function copyTrip(id: string, params?: CopyTripParams): Promise<string> {
  const body = (await request(`/trips/${id}/copy`, {
    method: "POST",
    body: params ? JSON.stringify(params) : undefined,
  })) as {
    tripId: string;
  };
  return body.tripId;
}

export type TripPatch = {
  title?: string;
  description?: string | null;
  maxDrivingHoursPerDay?: number;
  travelers?: Travelers | null;
  travelersNotes?: string | null;
  mustVisits?: MustVisitEntry[] | null;
  startLocation?: string | null;
  endLocation?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  interests?: string | null;
  archived?: boolean;
  headerNotes?: string | null;
  summaryNotes?: string | null;
};

export class TripPatchError extends Error {
  status: number;
  bookingPlaceName: string | null;
  bookingDate: string | null;
  moderation: boolean;
  constructor(
    status: number,
    message: string,
    bookingPlaceName: string | null = null,
    bookingDate: string | null = null,
    moderation = false,
  ) {
    super(message);
    this.status = status;
    this.bookingPlaceName = bookingPlaceName;
    this.bookingDate = bookingDate;
    this.moderation = moderation;
  }
}

export async function updateTrip(id: string, patch: TripPatch): Promise<Trip> {
  const res = await fetch(`/api/trips/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    let payload: {
      error?: string;
      bookingPlaceName?: string | null;
      bookingDate?: string | null;
      moderation?: boolean;
    } = {};
    try {
      payload = await res.json();
    } catch {
      // fall through with empty payload
    }
    throw new TripPatchError(
      res.status,
      payload.error ?? `Request failed: ${res.status}`,
      payload.bookingPlaceName ?? null,
      payload.bookingDate ?? null,
      payload.moderation ?? false,
    );
  }
  const body = (await res.json()) as { trip: Trip };
  return body.trip;
}

export async function updateDestinationName(
  tripId: string,
  destinationId: string,
  name: string
): Promise<{ id: string; name: string }> {
  const body = (await request(
    `/trips/${tripId}/destinations/${destinationId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }
  )) as { destination: { id: string; name: string } };
  return body.destination;
}

export async function updateDestinationNotes(
  tripId: string,
  destinationId: string,
  notes: string
): Promise<{ id: string; notes: string | null }> {
  const body = (await request(
    `/trips/${tripId}/destinations/${destinationId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ notes }),
    }
  )) as { destination: { id: string; notes: string | null } };
  return body.destination;
}

export async function updateDestinationDescription(
  tripId: string,
  destinationId: string,
  description: string
): Promise<{ id: string; description: string | null }> {
  const body = (await request(
    `/trips/${tripId}/destinations/${destinationId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ description }),
    }
  )) as { destination: { id: string; description: string | null } };
  return body.destination;
}

export async function updateDestinationCenterPlace(
  tripId: string,
  destinationId: string,
  centerPlaceId: string | null
): Promise<{ id: string; centerPlaceId: string | null }> {
  const body = (await request(
    `/trips/${tripId}/destinations/${destinationId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ centerPlaceId }),
    }
  )) as { destination: { id: string; centerPlaceId: string | null } };
  return body.destination;
}

export async function updateDestinationUserNotes(
  tripId: string,
  destinationId: string,
  userNotes: string | null
): Promise<{ id: string; userNotes: string | null }> {
  const body = (await request(
    `/trips/${tripId}/destinations/${destinationId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ userNotes }),
    }
  )) as { destination: { id: string; userNotes: string | null } };
  return body.destination;
}

export async function updateSegmentUserNotes(
  tripId: string,
  segmentId: string,
  userNotes: string | null
): Promise<{ id: string; userNotes: string | null }> {
  const body = (await request(
    `/trips/${tripId}/segments/${segmentId}/notes`,
    {
      method: "PATCH",
      body: JSON.stringify({ userNotes }),
    }
  )) as { segment: { id: string; userNotes: string | null } };
  return body.segment;
}

export async function regenerateDestinationPhoto(
  tripId: string,
  destinationId: string
): Promise<{ id: string; photoUrl: string | null }> {
  const body = (await request(
    `/trips/${tripId}/destinations/${destinationId}/photo/regenerate`,
    { method: "POST" }
  )) as { destination: { id: string; photoUrl: string | null } };
  return body.destination;
}

export type SegmentRouteResponse = {
  segmentId: string;
  fromDestinationId: string;
  toDestinationId: string;
  mode: "plane" | "car" | "train";
  polyline: [number, number][];
  distanceM: number | null;
  durationSec: number | null;
  cacheHit: boolean;
  source: string;
  roadSummary?: string | null;
};

export async function regenerateSegmentRoute(
  tripId: string,
  segmentId: string
): Promise<SegmentRouteResponse> {
  const body = (await request(
    `/trips/${tripId}/segments/${segmentId}/route/regenerate`,
    { method: "POST" }
  )) as { route: SegmentRouteResponse };
  return body.route;
}

export type SegmentPreferences = {
  avoidHighways: boolean | null;
  avoidTolls: boolean | null;
  avoidFerries: boolean | null;
};

export async function updateSegmentPreferences(
  tripId: string,
  segmentId: string,
  prefs: SegmentPreferences
): Promise<SegmentRouteResponse> {
  const body = (await request(
    `/trips/${tripId}/segments/${segmentId}/preferences`,
    { method: "PATCH", body: JSON.stringify(prefs) }
  )) as { route: SegmentRouteResponse };
  return body.route;
}

export async function toggleSegmentLockOrder(
  tripId: string,
  segmentId: string,
  lockOrder: boolean
): Promise<void> {
  await request(`/trips/${tripId}/segments/${segmentId}/lock-order`, {
    method: "PUT",
    body: JSON.stringify({ lockOrder }),
  });
}

export async function reoptimizeSegment(
  tripId: string,
  segmentId: string
): Promise<void> {
  await request(`/trips/${tripId}/segments/${segmentId}/reoptimize`, {
    method: "POST",
  });
}

export async function listCollaborators(tripId: string): Promise<Collaborator[]> {
  const body = (await request(`/trips/${tripId}/collaborators`)) as {
    collaborators: Collaborator[];
  };
  return body.collaborators;
}

export class InviteSendError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function inviteCollaborator(
  tripId: string,
  email: string,
  role: CollaboratorRole
): Promise<Invite> {
  const res = await fetch(`/api/trips/${tripId}/invites`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, role }),
  });
  if (!res.ok) {
    let payload: { error?: string } = {};
    try {
      payload = await res.json();
    } catch {
      // fall through with empty payload
    }
    throw new InviteSendError(
      res.status,
      payload.error ?? `Request failed: ${res.status}`
    );
  }
  const body = (await res.json()) as { invite: Invite };
  return body.invite;
}

export async function createLinkInvite(
  tripId: string,
  label: string,
  role: CollaboratorRole
): Promise<CreateLinkInviteResponse> {
  const res = await fetch(`/api/trips/${tripId}/invites`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ label, role }),
  });
  if (!res.ok) {
    let payload: { error?: string } = {};
    try {
      payload = await res.json();
    } catch {
      // fall through with empty payload
    }
    throw new InviteSendError(
      res.status,
      payload.error ?? `Request failed: ${res.status}`
    );
  }
  return (await res.json()) as CreateLinkInviteResponse;
}

export async function listPendingInvites(tripId: string): Promise<Invite[]> {
  const body = (await request(`/trips/${tripId}/invites`)) as {
    invites: Invite[];
  };
  return body.invites;
}

export async function revokePendingInvite(
  tripId: string,
  inviteId: string
): Promise<void> {
  await request(`/trips/${tripId}/invites/${inviteId}`, { method: "DELETE" });
}

export async function updateCollaboratorRole(
  tripId: string,
  userId: string,
  role: CollaboratorRole
): Promise<void> {
  await request(`/trips/${tripId}/collaborators/${userId}`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
}

export async function removeCollaborator(
  tripId: string,
  userId: string
): Promise<void> {
  await request(`/trips/${tripId}/collaborators/${userId}`, {
    method: "DELETE",
  });
}

export async function getCostState(
  tripId: string
): Promise<import("./chat").CostState> {
  const body = (await request(`/trips/${tripId}/cost`)) as {
    costState: import("./chat").CostState;
  };
  return body.costState;
}

export type CostCellPatch = {
  value: number;
  currency: string;
  state: "user" | "booking";
  /**
   * Phase 8 #10 — free-form label for user-added cost rows. When present, the
   * deriver renders it verbatim instead of humanizing the slug category. Only
   * set by the "+ add cost" UI; existing pinned cells (Phase 8.09 rate edits,
   * legacy LLM-pinned cells) leave it unset.
   */
  label?: string;
};

export type CostStatePatch = {
  assumptions?: import("./chat").Assumption[];
  userCurrencyOverride?: string | null;
  /**
   * Partial cell update: each provided key is written, `null` deletes,
   * unprovided keys remain untouched. Mirrors the worker's PATCH /cost
   * partial-cells contract (issue #65).
   */
  cells?: Record<string, CostCellPatch | null>;
  /**
   * Phase 10-01 slice 3 — flip the Cost-tab lock. When `true`, future PATCH
   * mutations on assumptions / cells / userCurrencyOverride are rejected
   * with HTTP 409 until a `{ locked: false }` PATCH unlocks the trip.
   */
  locked?: boolean;
};

export class CostStatePatchError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function updateCostState(
  tripId: string,
  patch: CostStatePatch
): Promise<import("./chat").CostState> {
  const res = await fetch(`/api/trips/${tripId}/cost`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    let payload: { error?: string } = {};
    try {
      payload = await res.json();
    } catch {
      // fall through with empty payload
    }
    throw new CostStatePatchError(
      res.status,
      payload.error ?? `Request failed: ${res.status}`
    );
  }
  const body = (await res.json()) as { costState: import("./chat").CostState };
  return body.costState;
}

/**
 * GET /api/trips/:id/cost/fx — read the cached FX rates without triggering a
 * fetch. Returns null when no rates have been cached yet (first Cost-tab
 * open, or the cache was invalidated by a reference-currency change).
 */
export async function getCostFx(
  tripId: string
): Promise<import("./chat").CostFx | null> {
  const body = (await request(`/trips/${tripId}/cost/fx`)) as {
    fx: import("./chat").CostFx | null;
  };
  return body.fx;
}

export class CostFxRefreshError extends Error {
  status: number;
  /** The previously-cached fx (may be null) — left intact on failure so the
   * UI can keep rendering last-known totals. */
  cachedFx: import("./chat").CostFx | null;
  constructor(
    status: number,
    message: string,
    cachedFx: import("./chat").CostFx | null
  ) {
    super(message);
    this.status = status;
    this.cachedFx = cachedFx;
  }
}

/**
 * POST /api/trips/:id/cost/fx/refresh — force a Frankfurter fetch on the
 * worker and persist the result. The worker leaves the cached fx intact on
 * Frankfurter failure; this helper surfaces both the error and the cached
 * value via CostFxRefreshError so the SPA can keep the dual-currency column
 * readable.
 */
export async function refreshCostFx(
  tripId: string
): Promise<import("./chat").CostFx> {
  const res = await fetch(`/api/trips/${tripId}/cost/fx/refresh`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    let payload: {
      error?: string;
      fx?: import("./chat").CostFx | null;
    } = {};
    try {
      payload = await res.json();
    } catch {
      // fall through with empty payload
    }
    throw new CostFxRefreshError(
      res.status,
      payload.error ?? `Request failed: ${res.status}`,
      payload.fx ?? null
    );
  }
  const body = (await res.json()) as { fx: import("./chat").CostFx };
  return body.fx;
}

export class CostSeedDefaultsError extends Error {
  status: number;
  /** The trip's existing cost state — left intact when the LLM call fails so
   * the SPA can keep rendering whatever's already there while the user
   * decides whether to retry. */
  costState: import("./chat").CostState | null;
  constructor(
    status: number,
    message: string,
    costState: import("./chat").CostState | null
  ) {
    super(message);
    this.status = status;
    this.costState = costState;
  }
}

export class CostResetError extends Error {
  status: number;
  /** The trip's pre-reset cost state — preserved on failure (the worker
   * persists nothing when the LLM call errors). The SPA can keep rendering
   * the old table while the user decides whether to retry. */
  costState: import("./chat").CostState | null;
  constructor(
    status: number,
    message: string,
    costState: import("./chat").CostState | null
  ) {
    super(message);
    this.status = status;
    this.costState = costState;
  }
}

/**
 * POST /api/trips/:id/cost/reset — wipe assumptions + cells + defaultsSeeded,
 * then re-run the seeder. Preserves `userCurrencyOverride`, `locked`, `fx`.
 * Rejects with 409 when the cost tab is locked. Throws CostResetError on
 * failure with the pre-reset cost state attached so the SPA can keep
 * rendering whatever's already there.
 */
export async function resetCostState(
  tripId: string
): Promise<import("./chat").CostState> {
  const res = await fetch(`/api/trips/${tripId}/cost/reset`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    let payload: {
      error?: string;
      costState?: import("./chat").CostState | null;
    } = {};
    try {
      payload = await res.json();
    } catch {
      // fall through with empty payload
    }
    throw new CostResetError(
      res.status,
      payload.error ?? `Request failed: ${res.status}`,
      payload.costState ?? null
    );
  }
  const body = (await res.json()) as { costState: import("./chat").CostState };
  return body.costState;
}

/**
 * POST /api/trips/:id/cost/seed-defaults — generate 3–5 starter assumptions
 * via Haiku 4.5 on first Cost-tab open (issue C7). Idempotent on the worker
 * side: a second call with `defaultsSeeded: true` already set returns the
 * unchanged state without re-running the LLM. Throws CostSeedDefaultsError
 * on transport / model failure with the existing cost state attached so the
 * SPA can surface a Retry button without losing prior content.
 */
export async function seedCostDefaults(
  tripId: string
): Promise<import("./chat").CostState> {
  const res = await fetch(`/api/trips/${tripId}/cost/seed-defaults`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    let payload: {
      error?: string;
      costState?: import("./chat").CostState | null;
    } = {};
    try {
      payload = await res.json();
    } catch {
      // fall through with empty payload
    }
    throw new CostSeedDefaultsError(
      res.status,
      payload.error ?? `Request failed: ${res.status}`,
      payload.costState ?? null
    );
  }
  const body = (await res.json()) as { costState: import("./chat").CostState };
  return body.costState;
}

/**
 * Per-user settings (issue #59). The cost calculator (#62, #64) uses
 * `referenceCurrency` as the fallback when a trip has no
 * `cost_state.userCurrencyOverride`. Defaults to USD when the user has
 * never opened the Settings modal.
 */
export type RouteDefaults = {
  avoidHighways: boolean;
  avoidTolls: boolean;
  avoidFerries: boolean;
};

export type UserSettings = {
  referenceCurrency: string;
  distanceUnit?: "km" | "mile" | null;
  defaultOrigin?: string | null;
  defaultTravelers?: {
    adults: number;
    children: number[];
    notes?: string;
  } | null;
  maxDrivingHoursPerDay?: number | null;
  language?: string | null;
  routeDefaults?: RouteDefaults | null;
};

export async function getUserSettings(): Promise<UserSettings> {
  const body = (await request("/me/settings")) as { settings: UserSettings };
  return body.settings;
}

export class UserSettingsPatchError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function updateUserSettings(
  patch: Partial<UserSettings>
): Promise<UserSettings> {
  const res = await fetch(`/api/me/settings`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    let payload: { error?: string } = {};
    try {
      payload = await res.json();
    } catch {
      // fall through with empty payload
    }
    throw new UserSettingsPatchError(
      res.status,
      payload.error ?? `Request failed: ${res.status}`
    );
  }
  const body = (await res.json()) as { settings: UserSettings };
  return body.settings;
}

export class NicknamePatchError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function updateNickname(
  nickname: string,
): Promise<{ nickname: string }> {
  const res = await fetch("/api/me/nickname", {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ nickname }),
  });
  if (!res.ok) {
    let payload: { error?: string } = {};
    try {
      payload = await res.json();
    } catch {}

    throw new NicknamePatchError(
      res.status,
      payload.error ?? `Request failed: ${res.status}`,
    );
  }
  const body = (await res.json()) as { user: { nickname: string } };
  return { nickname: body.user.nickname };
}

export class ProfilePatchError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function updateProfile(
  patch: { name: string },
): Promise<{ name: string }> {
  const res = await fetch("/api/me/profile", {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    let payload: { error?: string } = {};
    try {
      payload = await res.json();
    } catch {}
    throw new ProfilePatchError(
      res.status,
      payload.error ?? `Request failed: ${res.status}`,
    );
  }
  const body = (await res.json()) as { user: { name: string } };
  return { name: body.user.name };
}

export class AvatarUploadError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function uploadAvatar(
  blob: Blob,
): Promise<{ imageUrl: string }> {
  const res = await fetch("/api/me/avatar", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": blob.type },
    body: blob,
  });
  if (!res.ok) {
    let payload: { error?: string } = {};
    try {
      payload = await res.json();
    } catch {}
    throw new AvatarUploadError(
      res.status,
      payload.error ?? `Request failed: ${res.status}`,
    );
  }
  return (await res.json()) as { imageUrl: string };
}

export async function getPendingInvite(): Promise<InvitePending | null> {
  const res = await fetch(`/api/invitations/pending`, {
    credentials: "include",
  });
  if (res.status === 404 || res.status === 401) return null;
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return (await res.json()) as InvitePending;
}

export async function claimPendingInvite(): Promise<{ tripId: string | null }> {
  const res = await fetch(`/api/invitations/claim`, {
    method: "POST",
    credentials: "include",
  });
  if (res.status === 401) return { tripId: null };
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return (await res.json()) as { tripId: string | null };
}

export type CollabScopeKind = "trip" | "destination" | "segment" | "cost";

export type CollabMessage = {
  id: string;
  tripId: string;
  scopeKind: CollabScopeKind;
  scopeId: string | null;
  userId: string | null;
  authorName: string | null;
  authorImage: string | null;
  text: string;
  createdAt: string;
  heartCount: number;
  heartedByMe: boolean;
  readByMe: boolean;
};

export class CollabApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function listCollabMessages(
  tripId: string,
  scope?: { kind: string; id?: string },
): Promise<CollabMessage[]> {
  let url = `/trips/${tripId}/forum`;
  if (scope) {
    const params = new URLSearchParams({ scope_kind: scope.kind });
    if (scope.id) params.set("scope_id", scope.id);
    url += `?${params}`;
  }
  const body = (await request(url)) as { messages: CollabMessage[] };
  return body.messages;
}

export async function postCollabMessage(
  tripId: string,
  text: string,
  scopeKind: CollabScopeKind,
  scopeId?: string,
): Promise<CollabMessage> {
  const res = await fetch(`/api/trips/${tripId}/forum`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, scopeKind, scopeId }),
  });
  if (!res.ok) {
    let payload: { error?: string } = {};
    try {
      payload = await res.json();
    } catch {}
    throw new CollabApiError(res.status, payload.error ?? `Request failed: ${res.status}`);
  }
  const body = (await res.json()) as { message: CollabMessage };
  return body.message;
}

export async function deleteCollabMessage(
  tripId: string,
  messageId: string,
): Promise<void> {
  const res = await fetch(`/api/trips/${tripId}/forum/${messageId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    throw new CollabApiError(res.status, `Request failed: ${res.status}`);
  }
}

export async function markCollabRead(
  tripId: string,
  messageId: string,
): Promise<void> {
  const res = await fetch(`/api/trips/${tripId}/forum/${messageId}/read`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    throw new CollabApiError(res.status, `Request failed: ${res.status}`);
  }
}

export async function toggleCollabHeart(
  tripId: string,
  messageId: string,
): Promise<{ heartCount: number; heartedByMe: boolean }> {
  const res = await fetch(`/api/trips/${tripId}/forum/${messageId}/heart`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    throw new CollabApiError(res.status, `Request failed: ${res.status}`);
  }
  return res.json();
}

export type TripUnreadCount = { tripId: string; count: number; oldestUnreadId?: string | null };

export async function getUnreadForumCounts(): Promise<TripUnreadCount[]> {
  const body = (await request("/me/forum/unread")) as {
    counts: TripUnreadCount[];
  };
  return body.counts;
}

export type MessageCounts = { chat: number; collab: number };

export async function getMessageCounts(
  tripId: string,
  scopeKind: string,
  scopeId?: string,
): Promise<MessageCounts> {
  const params = new URLSearchParams({ scope_kind: scopeKind });
  if (scopeId) params.set("scope_id", scopeId);
  return (await request(`/trips/${tripId}/message-counts?${params}`)) as MessageCounts;
}
